import { Package, BarChart3, LogOut, Users, History, ShoppingCart, Settings, Crown, Download, TrendingUp, PieChart, PackageMinus, Key, Shield } from 'lucide-react';
import { NavLink } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useCompany } from '@/context/CompanyContext';
import { useSubscription } from '@/context/SubscriptionContext';
import { useManagementMode } from '@/hooks/useManagementMode';
import { usePlanAccess } from '@/hooks/usePlanAccess';
import { useAdminRole } from '@/hooks/useAdminRole';
import { ThemeToggle } from '@/components/ui/theme-toggle';
import { FontSizeControl } from '@/components/ui/font-size-control';
import { Sidebar, SidebarContent, SidebarGroup, SidebarGroupContent, SidebarGroupLabel, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarHeader, SidebarFooter, useSidebar } from '@/components/ui/sidebar';
import { Button } from '@/components/ui/button';
import chartFlowSidebarLogo from '@/assets/chart-flow-sidebar-logo.png';
const navigation = [{
  title: 'Tableau de bord',
  url: '/dashboard',
  icon: BarChart3
}, {
  title: 'Caisse',
  url: '/caisse',
  icon: ShoppingCart
}, {
  title: 'Analyse Ventes',
  url: '/analytics',
  icon: PieChart
}, {
  title: 'Retrait',
  url: '/retrait',
  icon: PackageMinus
}, {
  title: 'Analyse Retraits',
  url: '/retrait-analytics',
  icon: TrendingUp
}, {
  title: 'Mes Tableaux',
  url: '/statistics',
  icon: BarChart3
}, {
  title: 'Historique',
  url: '/history',
  icon: History
}, {
  title: 'Équipe',
  url: '/team',
  icon: Users,
  adminOnly: true
}, {
  title: 'Export/Import',
  url: '/export-import',
  icon: Download,
  adminOnly: true
}, {
  title: 'Abonnement',
  url: '/subscription',
  icon: Crown
}, {
  title: 'Paramètres',
  url: '/settings',
  icon: Settings
}, {
  title: 'Clés API',
  url: '/api-keys',
  icon: Key
}, {
  title: 'Admin',
  url: '/admin',
  icon: Shield,
  adminRoleOnly: true
}, {
  title: 'Journaux d\'Audit',
  url: '/audit-logs',
  icon: Shield,
  adminRoleOnly: true
}];
export function AppSidebar() {
  const {
    user,
    logout
  } = useAuth();
  const {
    userRole
  } = useCompany();
  const { hasAccess } = useSubscription();
  const { mode, isCaisseMode, isRetraitMode, loading: modeLoading } = useManagementMode();
  const { plan, hasPremiumAccess, hasStatsAccess } = usePlanAccess();
  const { isAdmin } = useAdminRole();
  const {
    open,
    setOpen
  } = useSidebar();
  
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);
  const canAccessCaisse = ['admin', 'member', 'vendeur'].includes(userRole);
  
  const handleMouseEnter = useCallback(() => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      setTimeoutId(null);
    }
    setOpen(true);
  }, [timeoutId, setOpen]);
  
  const handleMouseLeave = useCallback(() => {
    const id = setTimeout(() => {
      setOpen(false);
      setTimeoutId(null);
    }, 1000);
    setTimeoutId(id);
  }, [setOpen]);
  
  useEffect(() => {
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [timeoutId]);
  
  const handleLogout = () => logout();
  
  return <Sidebar 
    collapsible="icon" 
    className="overflow-hidden transition-all duration-300 ease-in-out" 
    variant="sidebar"
    onMouseEnter={handleMouseEnter}
    onMouseLeave={handleMouseLeave}
    role="navigation"
    aria-label="Menu de navigation principal"
  >
      <SidebarHeader className="border-b border-border">
        <div className="flex items-center gap-3 p-4 px-0 py-[27px]">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center">
            <img src={chartFlowSidebarLogo} alt="Chart-Flow Logo" className="w-6 h-6" />
          </div>
          {open && <div className="transition-all duration-300 ease-in-out">
              <h2 className="font-semibold text-foreground">Chart-Flow</h2>
              <p className="text-xs text-muted-foreground" role="status">
                {user?.role === 'admin' ? 'Admin' : 'Utilisateur'}
              </p>
            </div>}
        </div>
      </SidebarHeader>

      <SidebarContent className="text-sidebar-foreground bg-inherit overflow-x-hidden">
        <SidebarGroup>
          <SidebarGroupLabel className={`text-muted-foreground transition-all duration-300 ${!open ? 'sr-only' : ''}`}>
            Navigation
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu className="space-y-1" role="list">
              {navigation
                  .filter(item => {
                     // Les admins voient toujours la route admin, peu importe le mode ou le plan
                     if ((item as any).adminRoleOnly) return isAdmin;
                     
                     // Plan Stats : uniquement Mes Tableaux, Abonnement et Paramètres
                     if (plan === 'stats') {
                       const allowedUrls = ['/statistics', '/subscription', '/settings'];
                       return allowedUrls.includes(item.url);
                     }
                     
                     // Plan Premium : accès complet selon le mode
                     if (plan === 'premium') {
                       // Filtrer selon le mode de gestion choisi
                       if (isCaisseMode) {
                         // Mode caisse : masquer tout ce qui concerne les retraits
                         if (item.url === '/retrait' || item.url === '/retrait-analytics') return false;
                       }
                       if (isRetraitMode) {
                         // Mode retrait : masquer tout ce qui concerne la caisse
                         if (item.url === '/caisse' || item.url === '/analytics') return false;
                       }
                       
                       // Filtres d'accès habituels
                       if (item.url === '/caisse' || item.url === '/retrait' || item.url === '/retrait-analytics') return canAccessCaisse;
                       if (item.adminOnly) return userRole === 'admin';
                       return true;
                     }
                     
                     // Pas de plan défini : afficher navigation par défaut (selon le mode)
                     if (isCaisseMode && !modeLoading) {
                       if (item.url === '/retrait' || item.url === '/retrait-analytics') return false;
                     }
                     if (isRetraitMode && !modeLoading) {
                       if (item.url === '/caisse' || item.url === '/analytics') return false;
                     }
                     
                     if (item.url === '/caisse' || item.url === '/retrait' || item.url === '/retrait-analytics') return canAccessCaisse;
                     if (item.adminOnly) return userRole === 'admin';
                     return true;
                   })
                 .map(item => (
                <SidebarMenuItem 
                     key={item.title}
                     className="opacity-0 animate-fade-in"
                     style={{ 
                       animationDelay: `${navigation.indexOf(item) * 50}ms`,
                       animationFillMode: 'forwards'
                     }}
                     role="listitem"
                   >
                     <SidebarMenuButton asChild>
                         <NavLink
                           to={item.url}
                           aria-label={item.title}
                           className={({ isActive }) =>
                             `
                               flex items-center ${!open ? 'justify-center' : 'gap-3'} rounded-lg px-3 py-2 transition-all duration-200
                               hover:bg-muted dark:hover:bg-accent group relative overflow-hidden
                               ${
                                 isActive
                                   ? 'text-primary-foreground font-medium bg-primary/10 shadow-sm border-l-2 border-primary'
                                   : 'text-foreground hover:text-primary'
                               }
                             `
                           }
                         >
                           {({ isActive }) => (
                             <>
                               <item.icon className="h-4 w-4 transition-all duration-200 group-hover:scale-105 flex-shrink-0" aria-hidden="true" />
                               {open && (
                                 <span className="text-inherit transition-all duration-200 group-hover:translate-x-0.5 relative overflow-hidden min-w-0 flex-1">
                                   <span className="relative z-10 truncate block">{item.title}</span>
                                   <span className="absolute inset-0 bg-gradient-to-r from-transparent via-primary/10 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-500 ease-out" aria-hidden="true"></span>
                                 </span>
                               )}
                               <span className="sr-only">{isActive ? '(page actuelle)' : ''}</span>
                             </>
                           )}
                       </NavLink>
                     </SidebarMenuButton>
                   </SidebarMenuItem>
                ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        <div className={`${!open ? 'px-1 py-3' : 'p-4'} space-y-3 ${!hasAccess ? 'pb-24' : ''} transition-all duration-300`} role="contentinfo" aria-label="Informations utilisateur et contrôles">
          {open && <div className="mb-3 transition-all duration-300" role="status" aria-label="Informations du compte">
              <p className="text-sm font-medium text-foreground">{user?.email}</p>
              <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
            </div>}
          <div className="flex justify-center">
            <ThemeToggle compact={!open} />
          </div>
          <div className="flex justify-center mt-2">
            <FontSizeControl compact vertical={!open} />
          </div>
          <Button
            variant="outline"
            size={!open ? 'icon' : 'sm'}
            onClick={handleLogout}
            aria-label="Déconnexion"
            className={`${!open ? 'mx-auto h-9 w-9 p-0' : 'w-full'} transition-all duration-300 hover:bg-destructive hover:text-destructive-foreground`}
          >
            <LogOut className="h-4 w-4" aria-hidden="true" />
            {open && <span className="ml-2 transition-all duration-300">Déconnexion</span>}
          </Button>
        </div>
      </SidebarFooter>
    </Sidebar>;
}